import { useState, useEffect, useMemo } from "react";
import constants from "../../../../constants";
import { uniqBy } from "lodash";
import {
  archieveTasks,
  deleteTask,
  getAssigneeStaff,
  taskAction,
} from "repositories/task-management-repository";
import { getStorage, setStorage } from "utils";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
} from "utils/officeNavigationContext";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
const pageSize = 10;
const useTaskModule = (
  useAllTasks,
  isArchive = false,
  isFromDashboard = false,
) => {
  const {
    ownerList,
    commonOwnerData: selectedOwner,
    isOwner,
    effectiveOwnerId: accountOwnerId,
    switchNewAccountOwner: switchNewAccountOwnerCommon,
    ownerListLoading: OwnerLoading,
    isStaff,
  } = useAccountOwnerCommon();
  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");
  const cacheFilters = getStorage(constants.tasks.cache.myTaskFilter);
  const isGlobalFilterApply =
    cacheFilters?.offices?.length > 0 ||
    cacheFilters?.staff?.length > 0 ||
    cacheFilters?.status?.length > 0 ||
    cacheFilters?.priority?.length > 0;
  const [showLoader, setShowLoader] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [tasks, setTasks] = useState([]);

  const isAccessible = useMemo(() => {
    if (!isFromDashboard || isOwner) return true;
    if (!ownerList?.length || !selectedOwner) return false;
    return ownerList.some((o) => o.id === selectedOwner?.id);
  }, [isFromDashboard, isOwner, ownerList, selectedOwner]);
  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState(
    cacheFilters?.offices || [],
  );
  const [appliedStaffFilters, setAppliedStaffFilters] = useState(
    cacheFilters?.staff || [],
  );
  const [appliedStatusFilters, setAppliedStatusFilters] = useState(
    cacheFilters?.status || [],
  );
  const [appliedPriorityFilters, setAppliedPriorityFilters] = useState(
    cacheFilters?.priority || [],
  );
  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isAssigneeModal, setIsAssigneeModal] = useState(false);
  const [isDashBoardAssigneeModal, setIsDashboardAssigneeModal] =
    useState(false);
  const [assigneeList, setAssigneeList] = useState([]);
  const accountOwner = useMemo(() => [], []);
  const [officeFilter, setOfficeFilter] = useState(cacheFilters?.offices || []);
  const [staffFilter, setStaffFilter] = useState(cacheFilters?.staff || []);
  const [statusFilter, setStatusFilter] = useState(cacheFilters?.status || []);
  const [priorityFilter, setPriorityFilter] = useState(
    cacheFilters?.priority || [],
  );
  const [priority, setPriority] = useState({ id: 4, name: "All" });
  const [status, setStatus] = useState({ value: "", label: "All" });
  const [sortBy, setSortByState] = useState(2);
  const queryClient = useQueryClient();

  const { isLoading, data, refetch, isFetching } = useAllTasks(
    pageNumber,
    pageSize,
    appliedOfficeFilters,
    appliedStaffFilters,
    searchValue,
    isArchive,
    appliedPriorityFilters,
    appliedStatusFilters,
    sortBy,
    accountOwnerId,
    {
      enabled: isOwner || !!accountOwnerId,
    },
  );

  useEffect(() => {
    if (pageNumber === 1) {
      if (!isLoading && data && data.items) {
        setTasks(uniqBy([...data?.items], "id"));
        setTotalItems(data?.pagination?.totalItems);
        setTotalPages(data?.pagination?.totalPages);
      }
    } else if (!isLoading && data && data.items) {
      setTasks((prev) => uniqBy([...prev, ...data?.items], "id"));
      setTotalItems(data?.pagination?.totalItems);
      setTotalPages(data?.pagination?.totalPages);
    }
  }, [isLoading, data, pageNumber]);

  useEffect(() => {
    const officeContext = getOfficeNavigationContext();
    if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
      setOfficeFilter([officeContext.officeId]);
      setAppliedOfficeFilters([officeContext.officeId]);
      setPageNumber(1);
    }
  }, []);

  useEffect(() => {
    const syncOfficeFiltersFromContext = () => {
      const officeContext = getOfficeNavigationContext();
      if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
        setOfficeFilter([officeContext.officeId]);
        setAppliedOfficeFilters([officeContext.officeId]);
        setPageNumber(1);
        return;
      }
      const cached = getStorage(constants.tasks.cache.myTaskFilter) || {};
      setOfficeFilter(cached?.offices || []);
      setAppliedOfficeFilters(cached?.offices || []);
      setPageNumber(1);
    };
    window.addEventListener(
      OFFICE_NAV_CONTEXT_CHANGE_EVENT,
      syncOfficeFiltersFromContext,
    );
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        syncOfficeFiltersFromContext,
      );
  }, []);

  useEffect(() => {
    setPageNumber(1);
    if (priority.id) {
      if (priority.id < 4) {
        setAppliedPriorityFilters([priority.id]);
        setPriorityFilter([priority.id]);
      } else {
        setAppliedPriorityFilters([]);
        setPriorityFilter([]);
      }
    }
    if (status.value) {
      setAppliedStatusFilters([status.value]);
      setStatusFilter([status.value]);
    } else {
      setAppliedStatusFilters([]);
      setStatusFilter([]);
    }
  }, [priority, status]);

  useEffect(() => {
    queryClient.invalidateQueries([
      "/tasks/accountowner",
      pageNumber,
      pageSize,
      appliedOfficeFilters,
      appliedStaffFilters,
      searchValue,
      isArchive,
      appliedPriorityFilters,
      appliedStatusFilters,
      sortBy,
      accountOwnerId,
    ]);
    if (isOwner || !!accountOwnerId) {
      refetch();
    }
  }, [
    isArchive,
    sortBy,
    accountOwnerId,
    appliedPriorityFilters,
    appliedStatusFilters,
  ]);

  const resetFilter = () => {
    setOfficeFilter([]);
    setStaffFilter([]);
    setStatusFilter([]);
    setPriorityFilter([]);
    setAppliedOfficeFilters([]);
    setAppliedStaffFilters([]);
    setAppliedStatusFilters([]);
    setAppliedPriorityFilters([]);
    setPriority({ id: 4, name: "All" });
    setStatus({ value: "", label: "All" });
    setStorage(constants.tasks.cache.myTaskFilter, {
      offices: [],
      staff: [],
      status: [],
      priority: [],
    });
    setPageNumber(1);
  };

  const handleApplyFilters = () => {
    setAppliedOfficeFilters(officeFilter);
    setAppliedStaffFilters(staffFilter);
    setAppliedStatusFilters(statusFilter);
    setAppliedPriorityFilters(priorityFilter);
    setStorage(constants.tasks.cache.myTaskFilter, {
      offices: officeFilter,
      staff: staffFilter,
      status: statusFilter,
      priority: priorityFilter,
    });
    setPageNumber(1);
  };

  const handleSearchTerm = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.length >= 3) {
      setPageNumber(1);
      setSearchValue(value);
    } else if (value.length === 0) {
      setPageNumber(1);
      setSearchValue("");
    }
  };

  const StaffMembers = async (taskId, pageSize) => {
    setShowLoader(true);
    try {
      const res = await getAssigneeStaff(taskId, pageSize);
      if (res && res?.items?.length) {
        setAssigneeList(res?.items);
        setShowLoader(false);
      }
    } catch (e) {
      setShowLoader(false);
    }
  };

  const handleToggleAssigne = (taskId = "", pageSize) => {
    if (isAssigneeModal) {
      setIsAssigneeModal(false);
      setAssigneeList([]);
    } else {
      StaffMembers(taskId, pageSize);
      setIsAssigneeModal(true);
    }
  };

  const DashboardhandleToggleAssigne = (
    taskId = "",
    pageSize,
    isFromCommentScreen = false,
  ) => {
    if (isFromCommentScreen && isDashBoardAssigneeModal) {
      return;
    }
    if (isDashBoardAssigneeModal) {
      setIsDashboardAssigneeModal(false);
      setAssigneeList([]);
    } else {
      StaffMembers(taskId, pageSize);
      setIsDashboardAssigneeModal(true);
    }
  };

  const handleAction = async (taskId, TaskAction) => {
    try {
      const res = await taskAction(taskId, TaskAction);
      if (res?.status) {
        toast.success(res.message);
        if (TaskAction === "Cancel") {
          setTasks((prevTasks) => {
            return prevTasks.map((task) =>
              task.id === taskId ? { ...task, overallStatus: 7 } : task,
            );
          });
        } else {
          setTasks((prevTasks) => {
            return prevTasks.filter((task) => task.id !== taskId);
          });
        }
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteAction = async (taskId) => {
    try {
      const res = await deleteTask(taskId);
      if (res?.status) {
        toast.success(res.message);
        setTasks((prevTask) => {
          const updatetask = prevTask?.filter((task) => task?.id !== taskId);
          return updatetask;
        });
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleArchiveApi = async (taskId, action) => {
    try {
      const res = await archieveTasks(taskId, action);
      if (res?.status) {
        toast.success(res.message);
        setTasks((prevTask) => {
          const updatetask = prevTask?.filter((task) => task?.id !== taskId);
          return updatetask;
        });
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  /**
   * Sort changes always restart at page 1. Infinite scroll then requests later pages.
   * @param {number} nextSortBy
   */
  const setSortBy = (nextSortBy) => {
    window.scrollTo(0, 0);
    const dashboardList = document.getElementById("scrollableDivTwo");
    if (dashboardList) dashboardList.scrollTop = 0;

    const sortChanged = nextSortBy !== sortBy;
    if (!sortChanged && pageNumber === 1) return;

    setPageNumber(1);
    if (sortChanged) setSortByState(nextSortBy);
  };

  const isAnyLoading = isLoading || isFetching || showLoader || OwnerLoading;
  const isFilterApply = isGlobalFilterApply || appliedOfficeFilters?.length > 0;

  return {
    isLoading: isAnyLoading,
    tasks,
    totalItems,
    searchValue,
    inputValue,
    isAssigneeModal,
    assigneeList,
    showLoader,
    accountOwner,
    selectedOwner,
    accountOwnerId,
    officeFilter,
    staffFilter,
    statusFilter,
    priorityFilter,
    isFilterApply,
    sortBy,
    priority,
    status,
    pageNumber,
    ownerList,
    totalPages,
    isDashBoardAssigneeModal,
    setStatus,
    setPriority,
    setTasks,
    setPageNumber,
    handleSearchTerm,
    handleToggleAssigne,
    setAppliedOfficeFilters,
    setAppliedStaffFilters,
    setAppliedStatusFilters,
    setAppliedPriorityFilters,
    setSelectedOwner: switchNewAccountOwnerCommon,
    handleOwnerSelection: switchNewAccountOwnerCommon,
    handleOnChange: switchNewAccountOwnerCommon,
    setOfficeFilter,
    handleApplyFilters,
    setStaffFilter,
    resetFilter,
    setStatusFilter,
    setPriorityFilter,
    handleAction,
    deleteAction,
    handleArchiveApi,
    StaffMembers,
    setSortBy,
    refetch,
    DashboardhandleToggleAssigne,
    isOwner,
    isStaff,
    isAccessible,
  };
};

export default useTaskModule;
